import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Smile, Meh, Frown, Zap, Cloud, Send, SkipForward } from "lucide-react";

const MOODS = [
  { id: "joyeux", label: "Joyeux", icon: Smile, color: "#4A8B6F" },
  { id: "calme", label: "Calme", icon: Meh, color: "#2563EB" },
  { id: "fatigue", label: "Fatigué", icon: Cloud, color: "#6B7280" },
  { id: "agite", label: "Agité", icon: Zap, color: "#D97706" },
  { id: "triste", label: "Triste", icon: Frown, color: "#DB2777" },
];

export default function SessionNoteForm({ patientId, exerciseName = "", onDone }) {
  const [participation, setParticipation] = useState(3);
  const [mood, setMood] = useState("joyeux");
  const [observation, setObservation] = useState("");
  const [informFamily, setInformFamily] = useState(false);
  const [summary, setSummary] = useState(
    exerciseName ? `Un beau moment de partage autour de « ${exerciseName} » aujourd'hui.` : "Un beau moment de partage aujourd'hui."
  );
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/patients/${patientId}/notes`, {
        participation, mood, observation, exercise_name: exerciseName,
        inform_family: informFamily, family_summary: summary,
      });
      toast.success(informFamily ? "Note enregistrée et famille informée" : "Note de suivi enregistrée");
      onDone();
    } catch {
      toast.error("Enregistrement impossible.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-full max-w-xl mx-auto bg-white rounded-3xl border-4 border-emerald-700/10 p-6 sm:p-8 soft-in" data-testid="session-note-form">
      <h2 className="font-serif-display text-2xl font-normal text-gray-800 mb-1">Note de suivi</h2>
      <p className="text-gray-500 mb-6">Réservée à l'équipe soignante{exerciseName ? ` — ${exerciseName}` : ""}.</p>

      <label className="block font-patient font-bold text-gray-700 mb-2">Niveau de participation</label>
      <div className="flex gap-2 mb-6">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} onClick={() => setParticipation(n)} data-testid={`participation-${n}`}
            className={`flex-1 h-14 rounded-xl font-patient font-black text-xl border-4 transition-colors ${
              participation >= n ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-gray-200 text-gray-400"}`}>
            {n}
          </button>
        ))}
      </div>

      <label className="block font-patient font-bold text-gray-700 mb-2">Humeur du résident</label>
      <div className="flex flex-wrap gap-2 mb-6">
        {MOODS.map((m) => {
          const Icon = m.icon;
          const sel = mood === m.id;
          return (
            <button key={m.id} onClick={() => setMood(m.id)} data-testid={`mood-${m.id}`}
              style={sel ? { backgroundColor: m.color, borderColor: m.color, color: "#fff" } : {}}
              className={`flex items-center gap-2 h-12 px-4 rounded-xl border-4 font-patient font-bold ${sel ? "" : "bg-white border-gray-200 text-gray-500"}`}>
              <Icon className="w-5 h-5" /> {m.label}
            </button>
          );
        })}
      </div>

      <label className="block font-patient font-bold text-gray-700 mb-2">Observations libres</label>
      <Textarea value={observation} onChange={(e) => setObservation(e.target.value)}
        placeholder="Ce que vous avez observé pendant l'activité…" className="mb-6 min-h-[100px]" data-testid="observation-input" />

      <div className="rounded-2xl bg-amber-50 border-2 border-amber-200 p-4 mb-6">
        <label className="flex items-center gap-3 cursor-pointer">
          <Switch checked={informFamily} onCheckedChange={setInformFamily} data-testid="inform-family-switch" />
          <span className="font-patient font-bold text-gray-700">Informer la famille</span>
        </label>
        {informFamily && (
          <div className="mt-3">
            <p className="text-sm text-gray-500 mb-1">Résumé bienveillant partagé sur l'espace famille :</p>
            <Textarea value={summary} onChange={(e) => setSummary(e.target.value)} className="bg-white" data-testid="family-summary-input" />
          </div>
        )}
      </div>

      <div className="flex gap-3">
        <button onClick={submit} disabled={saving} data-testid="save-note-button"
          className="flex-1 h-16 rounded-2xl bg-emerald-700 text-white font-patient font-bold text-xl flex items-center justify-center gap-2 active:scale-95 transition-transform">
          <Send className="w-6 h-6" /> {saving ? "Enregistrement…" : "Enregistrer"}
        </button>
        <button onClick={onDone} data-testid="skip-note-button"
          className="h-16 px-5 rounded-2xl bg-white border-4 border-gray-200 text-gray-500 font-patient font-bold flex items-center gap-2 active:scale-95 transition-transform">
          <SkipForward className="w-5 h-5" /> Passer
        </button>
      </div>
    </div>
  );
}
